/**
 * Verification queue — the operational heart of the admin dashboard.
 *
 * Layout:
 *   - Left: filterable list of pending / in-review verifications.
 *   - Right: detail panel for the selected verification with:
 *       * Student card (name, school, major, contact)
 *       * Submitted documents (thumbnail links)
 *       * Campaign story preview
 *       * Automated screening card
 *           TODO(backend): National Student Clearinghouse auto-check
 *           TODO(backend): objectionable-word scanner
 *       * Ops checklist:
 *           [ ] Reviewed for objectionable content
 *           ( ) New student / Returning student
 *           [ ] Confirmed under annual cap
 *       * Decision buttons: Approve / Reject / Request info
 *       * Request-info form + thread history
 *
 * All state here is client-only. Approving/rejecting/requesting info shows
 * a toast and removes the row from the pending list (in-session only).
 */

import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileText, ShieldCheck, ShieldAlert, User, Mail, School, RefreshCw,
  Search, Paperclip, ChevronRight, GraduationCap, PlayCircle,
} from "lucide-react";
import {
  SectionHeader, StatusBadge, AdminButton, AdminCheckbox, EmptyState,
} from "./_ui";
import { PENDING_VERIFICATIONS, OBJECTIONABLE_WORDS_SAMPLE } from "../../data/adminMock";
import { checkEnrollment, NSC_STATUS_META } from "../../lib/nscMock";

const fmt$ = (n) => `$${Number(n).toLocaleString()}`;

const FILTERS = [
  { key: "all",             label: "All",             predicate: () => true },
  { key: "pending",         label: "Pending",         predicate: (v) => v.status === "pending" },
  { key: "in-review",       label: "In review",       predicate: (v) => v.status === "in-review" },
  { key: "info-requested",  label: "Info requested",  predicate: (v) => v.status === "info-requested" },
];

// Simple client-side scanner used as a placeholder — the real one will be
// server-side per the backend TODO.
const scanForObjectionable = (text) => {
  const t = (text || "").toLowerCase();
  return OBJECTIONABLE_WORDS_SAMPLE.filter((w) => t.includes(w));
};

export const AdminVerifications = () => {
  const [queue, setQueue] = useState(PENDING_VERIFICATIONS);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(PENDING_VERIFICATIONS[0]?.id || null);
  const [checks, setChecks] = useState({}); // { [id]: { objectionable: bool, capOk: bool, isReturning: bool|null } }
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [nscRunning, setNscRunning] = useState(false);

  const visible = useMemo(() => queue.filter(FILTERS.find((f) => f.key === filter).predicate), [queue, filter]);
  const selected = queue.find((v) => v.id === selectedId) || visible[0];
  const c = checks[selected?.id] || { objectionable: false, capOk: false, isReturning: null };

  // TODO(backend): both hits should come from a server-side scanner that owns
  // the objectionable-word list. The client-side pass is only a hint here.
  const flaggedWords = selected ? scanForObjectionable(selected.story) : [];
  const capOk = selected ? selected.annualRaisedToDate + selected.goal <= selected.annualCap : true;

  const setCheck = (patch) =>
    setChecks((prev) => ({
      ...prev,
      [selected.id]: { ...(prev[selected.id] || {}), ...patch },
    }));

  const readyToDecide =
    c.objectionable === true &&
    c.capOk === true &&
    c.isReturning !== null;

  // Fires the (mocked) NSC lookup and patches the selected verification with
  // the result. In production this hits POST /api/verifications/nsc/check.
  const runNscCheck = async () => {
    if (!selected) return;
    setNscRunning(true);
    try {
      const result = await checkEnrollment({
        fullName: selected.name,
        school: selected.school,
        gradYear: selected.major,
      });
      setQueue((q) => q.map((v) => v.id === selected.id ? {
        ...v,
        nscStatus: result.status,
        nscLastCheckedAt: result.checkedAt,
        nscConfidence: result.confidence,
        nscReasonText: result.reasonText || null,
        nscRecords: result.records || [],
      } : v));
      const meta = NSC_STATUS_META[result.status] || NSC_STATUS_META["unavailable"];
      toast(`NSC check: ${meta.label}`, { description: meta.desc });
    } catch (e) {
      toast.error("NSC check failed. Try again.");
    } finally {
      setNscRunning(false);
    }
  };

  const removeFromQueue = () => {
    setQueue((q) => {
      const next = q.filter((v) => v.id !== selected.id);
      setSelectedId(next[0]?.id || null);
      return next;
    });
    setChecks((prev) => {
      const next = { ...prev };
      delete next[selected.id];
      return next;
    });
  };

  const onApprove = () => {
    // TODO(backend): PATCH /api/verifications/:id { status: "approved" }
    toast.success(`${selected.name} approved`, { description: "Campaign is now live." });
    removeFromQueue();
  };

  const onReject = () => {
    // TODO(backend): PATCH /api/verifications/:id { status: "rejected", reason }
    toast(`${selected.name} rejected`, {
      description: "Student notified. Record archived for 90 days.",
    });
    removeFromQueue();
  };

  const onRequestInfo = () => {
    // TODO(backend): POST /api/verifications/:id/info-requests { message, attachments[] }
    //   - Attachments upload to object storage.
    //   - Full request thread is stored on the student's campaign record.
    if (!infoMessage.trim()) {
      toast.error("Add a note explaining what you need.");
      return;
    }
    toast.success("Info request sent", {
      description: `${selected.name} was emailed. Thread saved to their record.`,
    });
    setQueue((q) =>
      q.map((v) =>
        v.id === selected.id
          ? {
              ...v,
              status: "info-requested",
              infoRequests: [
                ...v.infoRequests,
                { at: new Date().toISOString().slice(0, 10), by: "Terri", message: infoMessage, awaitingReply: true },
              ],
            }
          : v,
      ),
    );
    setInfoMessage("");
    setShowInfoForm(false);
  };

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Verification queue"
        title="Approve students, keep the platform trustworthy."
        description="Every student is reviewed once before their campaign goes live. Approve, reject, or request more information — every action is logged."
        action={
          <AdminButton variant="secondary" size="sm" testid="refresh-queue">
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} /> Refresh
          </AdminButton>
        }
      />

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2" role="tablist">
        {FILTERS.map((f) => {
          const count = queue.filter(f.predicate).length;
          const isActive = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition ${
                isActive
                  ? "bg-ink text-sand"
                  : "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40"
              }`}
            >
              {f.label}
              <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] ${
                isActive ? "bg-sand/20 text-sand" : "bg-ink/10 text-ink"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
        <div className="ml-auto hidden items-center gap-2 rounded-full bg-sand px-3 py-1.5 ring-1 ring-inset ring-ink/15 sm:inline-flex">
          <Search className="h-3.5 w-3.5 text-ink/50" strokeWidth={1.75} />
          <input
            type="search"
            placeholder="Search by name or school"
            className="w-56 bg-transparent text-xs text-ink outline-none placeholder:text-ink/40"
            data-testid="verifications-search"
          />
        </div>
      </div>

      {/* Two-pane list + detail */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* List */}
        <div className="lg:col-span-5">
          {visible.length === 0 ? (
            <EmptyState title="Queue clear" description="Nothing waiting for review. Nice work." />
          ) : (
            <ul className="divide-y divide-ink/10 overflow-hidden rounded-lg border border-ink/10 bg-sand">
              {visible.map((v) => {
                const isSelected = selected?.id === v.id;
                return (
                  <li key={v.id}>
                    <button
                      type="button"
                      data-testid={`verify-row-${v.id}`}
                      onClick={() => { setSelectedId(v.id); setShowInfoForm(false); }}
                      className={`flex w-full items-center gap-4 px-5 py-4 text-left transition ${
                        isSelected ? "bg-ink/5" : "hover:bg-ink/[0.03]"
                      }`}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tangerine/20 font-serif text-sm text-tangerine ring-1 ring-tangerine/40">
                        {v.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium text-ink">{v.name}</p>
                          {v.isReturningStudent && (
                            <span className="rounded-full bg-cobalt/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cobalt">Returning</span>
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-xs text-ink/60">
                          {v.school} · {v.category} · {fmt$(v.goal)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <StatusBadge status={v.status} />
                        <span className="text-[10px] text-ink/50">Submitted {v.submittedAt}</span>
                      </div>
                      <ChevronRight className={`h-4 w-4 shrink-0 ${isSelected ? "text-ink" : "text-ink/30"}`} strokeWidth={1.75} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {!selected ? (
            <EmptyState title="Nothing selected" description="Pick a verification from the list to review." />
          ) : (
            <div className="space-y-6 rounded-lg border border-ink/10 bg-sand p-6 lg:p-8">
              {/* Student card */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl leading-tight text-ink">{selected.name}</h3>
                  <div className="mt-2 grid grid-cols-1 gap-1 text-sm text-ink/70 sm:grid-cols-2">
                    <div className="flex items-center gap-2"><School className="h-3.5 w-3.5" strokeWidth={1.75} /> {selected.school}</div>
                    <div className="flex items-center gap-2"><User  className="h-3.5 w-3.5" strokeWidth={1.75} /> {selected.major}</div>
                    <div className="flex items-center gap-2"><Mail  className="h-3.5 w-3.5" strokeWidth={1.75} /> {selected.email}</div>
                    <div className="flex items-center gap-2 text-ink"><span className="font-serif">{fmt$(selected.goal)}</span> <span className="text-ink/50">goal</span></div>
                  </div>
                </div>
                <StatusBadge status={selected.status} />
              </div>

              {/* Story preview */}
              <div className="rounded-md border border-ink/10 bg-beige/60 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Their story</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/80">{selected.story}</p>
              </div>

              {/* Documents */}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Submitted documents</p>
                <ul className="mt-2 space-y-2">
                  {selected.documents.map((d) => (
                    <li key={d.name} className="flex items-center justify-between rounded-md border border-ink/10 bg-beige/60 px-3 py-2">
                      <div className="flex items-center gap-2 text-sm text-ink">
                        <FileText className="h-4 w-4 text-ink/60" strokeWidth={1.75} />
                        {d.name}
                      </div>
                      <span className="text-xs text-ink/50">Uploaded {d.uploadedAt}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Trust signals + automated screening */}
              <div className="space-y-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Trust signals</p>

                {/* NSC — hero card */}
                <div className="rounded-md border border-ink/10 bg-beige/60 p-4" data-testid="nsc-card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <GraduationCap className={`h-4 w-4 mt-0.5 text-${(NSC_STATUS_META[selected.nscStatus] || NSC_STATUS_META["not-attempted"]).tone}`} strokeWidth={1.75} />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-ink">National Student Clearinghouse</p>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 ring-inset bg-${(NSC_STATUS_META[selected.nscStatus] || NSC_STATUS_META["not-attempted"]).tone}/15 text-${(NSC_STATUS_META[selected.nscStatus] || NSC_STATUS_META["not-attempted"]).tone} ring-${(NSC_STATUS_META[selected.nscStatus] || NSC_STATUS_META["not-attempted"]).tone}/30`}>
                            {(NSC_STATUS_META[selected.nscStatus] || NSC_STATUS_META["not-attempted"]).label}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-ink/70">
                          {(NSC_STATUS_META[selected.nscStatus] || NSC_STATUS_META["not-attempted"]).desc}
                        </p>
                        {selected.nscReasonText && (
                          <p className="mt-1 text-xs italic text-ink/60">{selected.nscReasonText}</p>
                        )}
                        {selected.nscRecords && selected.nscRecords.length > 0 && (
                          <ul className="mt-2 space-y-0.5 text-xs text-ink/70">
                            {selected.nscRecords.map((r, i) => (
                              <li key={i}>
                                {r.school} · {r.classLevel} · {r.level === "F" ? "Full-time" : r.level === "H" ? "Half-time" : r.level} · {r.begin} → {r.end}
                              </li>
                            ))}
                          </ul>
                        )}
                        {selected.nscLastCheckedAt && (
                          <p className="mt-1 text-[10px] uppercase tracking-wide text-ink/50">
                            Checked {new Date(selected.nscLastCheckedAt).toLocaleString()}
                            {selected.nscConfidence != null && ` · ${Math.round(selected.nscConfidence * 100)}% confidence`}
                          </p>
                        )}
                      </div>
                    </div>
                    <AdminButton
                      size="sm"
                      variant="secondary"
                      onClick={runNscCheck}
                      disabled={nscRunning}
                      testid="run-nsc-check"
                    >
                      {nscRunning ? (
                        <><RefreshCw className="h-3 w-3 animate-spin" strokeWidth={2} /> Checking…</>
                      ) : (
                        <><PlayCircle className="h-3 w-3" strokeWidth={2} />
                          {selected.nscStatus === "not-attempted" ? "Run NSC check" : "Re-run"}
                        </>
                      )}
                    </AdminButton>
                  </div>
                  <p className="mt-3 text-[11px] italic text-ink/50">
                    Manual review is always available alongside NSC — even on a "verified" result, ops may choose to double-check during pilot.
                  </p>
                </div>

                {/* Smaller trust signals row */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {/* .edu email flag */}
                  <div className="rounded-md border border-ink/10 bg-beige/60 p-4" data-testid="edu-email-card">
                    <div className="flex items-center gap-2">
                      {selected.isEduEmail ? (
                        <ShieldCheck className="h-4 w-4 text-grass" strokeWidth={1.75} />
                      ) : (
                        <ShieldAlert className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
                      )}
                      <p className="text-sm font-medium text-ink">
                        {selected.isEduEmail ? ".edu email on file" : "Non-.edu email"}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-ink/60 break-all">{selected.email}</p>
                    <p className="mt-1 text-[11px] italic text-ink/50">
                      Soft trust signal only. Never a substitute for NSC.
                    </p>
                  </div>

                  {/* Content screen */}
                  <div className="rounded-md border border-ink/10 bg-beige/60 p-4" data-testid="content-screen-card">
                    <div className="flex items-center gap-2">
                      {flaggedWords.length === 0 ? (
                        <ShieldCheck className="h-4 w-4 text-grass" strokeWidth={1.75} />
                      ) : (
                        <ShieldAlert className="h-4 w-4 text-scarlet" strokeWidth={1.75} />
                      )}
                      <p className="text-sm font-medium text-ink">Content screen</p>
                    </div>
                    <p className="mt-1 text-xs text-ink/60">
                      {flaggedWords.length === 0
                        ? "No flagged words detected in story."
                        : `Flagged: ${flaggedWords.join(", ")}`}
                    </p>
                    <p className="mt-1 text-[11px] italic text-ink/50">
                      TODO(backend): server-side scan against ops-managed list.
                    </p>
                  </div>

                  {/* Student certification — legally binding attestation */}
                  <div className="rounded-md border border-ink/10 bg-beige/60 p-4" data-testid="student-cert-card">
                    <div className="flex items-center gap-2">
                      {selected.studentCertifiedAt ? (
                        <ShieldCheck className="h-4 w-4 text-grass" strokeWidth={1.75} />
                      ) : (
                        <ShieldAlert className="h-4 w-4 text-scarlet" strokeWidth={1.75} />
                      )}
                      <p className="text-sm font-medium text-ink">Grant-use certification</p>
                    </div>
                    <p className="mt-1 text-xs text-ink/60">
                      {selected.studentCertifiedAt
                        ? `Signed at signup — ${new Date(selected.studentCertifiedAt).toLocaleString()}`
                        : "Missing — student must re-certify before approval."}
                    </p>
                    <p className="mt-1 text-[11px] italic text-ink/50">
                      Legally binding. Substitutes for receipts on grants ≤ $500.
                    </p>
                  </div>
                </div>
              </div>

              {/* Ops checklist */}
              <div className="rounded-md border border-ink/10 bg-beige/60 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Reviewer checklist</p>
                <div className="mt-3 space-y-3">
                  <AdminCheckbox
                    testid="chk-objectionable"
                    checked={c.objectionable}
                    onChange={(v) => setCheck({ objectionable: v })}
                    label="I have reviewed the story for objectionable content."
                    hint="Follow the current ops list (Notion doc)."
                  />
                  <div>
                    <p className="text-sm text-ink">Is this a new or returning student?</p>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        data-testid="chk-new"
                        onClick={() => setCheck({ isReturning: false })}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                          c.isReturning === false ? "bg-ink text-sand" : "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40"
                        }`}
                      >
                        New student
                      </button>
                      <button
                        type="button"
                        data-testid="chk-returning"
                        onClick={() => setCheck({ isReturning: true })}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                          c.isReturning === true ? "bg-ink text-sand" : "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40"
                        }`}
                      >
                        Returning
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-ink/50">
                      Record shows: {selected.isReturningStudent ? "Previously verified" : "First campaign"}.
                    </p>
                  </div>
                  <AdminCheckbox
                    testid="chk-cap"
                    checked={c.capOk}
                    onChange={(v) => setCheck({ capOk: v })}
                    label={
                      capOk
                        ? `Confirmed under annual cap (${fmt$(selected.annualRaisedToDate)} raised of ${fmt$(selected.annualCap)}).`
                        : `Warning — this request would exceed the annual cap.`
                    }
                    hint={capOk ? "Cap allows this campaign." : "Escalate before approving."}
                  />
                </div>
              </div>

              {/* Decision */}
              <div className="flex flex-wrap items-center gap-3">
                <AdminButton
                  variant="approve"
                  onClick={onApprove}
                  disabled={!readyToDecide}
                  testid="btn-approve"
                >
                  Approve & publish
                </AdminButton>
                <AdminButton
                  variant="reject"
                  onClick={onReject}
                  disabled={!readyToDecide}
                  testid="btn-reject"
                >
                  Reject
                </AdminButton>
                <AdminButton
                  variant="info"
                  onClick={() => setShowInfoForm((s) => !s)}
                  testid="btn-request-info"
                >
                  Request info
                </AdminButton>
                {!readyToDecide && (
                  <span className="text-xs text-ink/50">Complete the checklist to enable decisions.</span>
                )}
              </div>

              {/* Info-request form */}
              {showInfoForm && (
                <div className="rounded-md border border-tangerine/40 bg-tangerine/5 p-4" data-testid="info-request-form">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
                    Request more information
                  </p>
                  <textarea
                    value={infoMessage}
                    onChange={(e) => setInfoMessage(e.target.value)}
                    rows={3}
                    placeholder="Explain what you need. Example: 'Please upload proof of current-semester enrollment (NSC report).'"
                    className="mt-2 w-full rounded-md border border-ink/15 bg-sand p-3 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-tangerine/40"
                    data-testid="info-request-textarea"
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <button type="button" className="inline-flex items-center gap-1 text-xs text-ink/60 hover:text-ink">
                      <Paperclip className="h-3.5 w-3.5" strokeWidth={1.75} /> Attach document
                    </button>
                    <AdminButton size="sm" onClick={onRequestInfo} testid="btn-send-info">
                      Send to student
                    </AdminButton>
                  </div>
                </div>
              )}

              {/* Request thread history */}
              {selected.infoRequests.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
                    Information request thread
                  </p>
                  <ul className="mt-2 space-y-2">
                    {selected.infoRequests.map((r, i) => (
                      <li key={i} className="rounded-md border border-ink/10 bg-beige/60 p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-ink">{r.by} · {r.at}</p>
                          {r.awaitingReply && (
                            <span className="rounded-full bg-tangerine/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-tangerine">
                              Awaiting reply
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-ink/80">{r.message}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
